import React, { useState, useEffect } from 'react';
import productsService from '../../services/api/productsService';
import { formatCurrency } from '../../utils/invoiceHelpers';

const CategoryProductsModal = ({ category, isOpen, onClose }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategoryProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all products for current user, then filter by category on frontend
      const response = await productsService.getProducts();
      
      // Response is the products array directly, not wrapped in a products field
      const allProducts = Array.isArray(response) ? response : [];
      
      // Filter products by category
      const categoryProducts = allProducts.filter(product => {
        // Match by category ID (PostgreSQL integer ID)
        return product.categoryId === category.id ||            // Database ID match (primary)
               product.categoryCode === category.categoryCode || // Code match
               product.category === category.name ||            // Name match
               product.categoryName === category.name;          // Alt name match
      });
      setProducts(categoryProducts);
    } catch (err) {
      // Only show error for actual failures, not empty results
      if (err.response?.status !== 404) {
        setError('Failed to load products');
        console.error('Error fetching category products:', err);
      } else {
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
    };

    if (isOpen && category) {
      fetchCategoryProducts();
    }
  }, [isOpen, category]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Products in "{category?.name}"
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {products.length} {products.length === 1 ? 'product' : 'products'} found
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="mt-3 text-sm text-gray-600">No products found</p>
              <p className="text-xs text-gray-500 mt-1">Products assigned to this category will appear here</p>
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <div className="space-y-3">
              {products.map(product => (
                <div
                  key={product.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {product.name}
                      </h3>
                      {product.sku && (
                        <p className="text-sm text-gray-500">
                          SKU: {product.sku}
                        </p>
                      )}
                      {product.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-medium text-gray-900">
                        {formatCurrency(product.price || 0)}
                      </p>
                      {product.stockQuantity !== undefined && (
                        <p className="text-sm text-gray-500">
                          Stock: {product.stockQuantity}
                        </p>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                        product.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Mobile Friendly */}
        <div className="border-t px-4 py-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryProductsModal;